import { apiClient } from './api';
import { VirtualDrone, VirtualDroneListResponse, VirtualDroneSoftware } from '../types/virtualDrone';

const BASE = '/api/v1/drones/virtual';

/**
 * Operator-owned virtual (simulated) drones (issue #322). All routes are scoped
 * to the authenticated operator server-side; the id_token is attached by the
 * ApiClient request interceptor.
 */
export const virtualDroneService = {
  /** List the operator's virtual drones. */
  async list(): Promise<VirtualDroneListResponse> {
    return apiClient.get<VirtualDroneListResponse>(BASE);
  },

  /** Create a virtual drone. Name is optional. */
  async create(name?: string): Promise<VirtualDrone> {
    const body = name && name.trim() ? { name: name.trim() } : {};
    return apiClient.post<VirtualDrone>(BASE, body);
  },

  /** What the drone's simulator runs, and whether an update is available (#791). */
  async software(id: string): Promise<VirtualDroneSoftware> {
    return apiClient.get<VirtualDroneSoftware>(`${BASE}/${id}/software`);
  },

  /**
   * Update the drone's simulator onto the pinned images (#791). Accepted at
   * once; it runs on the server, and `software` says how it went.
   */
  async update(id: string): Promise<void> {
    await apiClient.post<null>(`${BASE}/${id}/update`, {});
  },

  /** Stop the drone's simulator and keep the drone, on the ground (#802). */
  async switchOff(id: string): Promise<void> {
    await apiClient.post<null>(`${BASE}/${id}/switch-off`, {});
  },

  /** Start a switched-off drone's simulator again (#802). Accepted at once; `software` says how it went. */
  async switchOn(id: string): Promise<void> {
    await apiClient.post<null>(`${BASE}/${id}/switch-on`, {});
  },

  /** Delete one of the operator's virtual drones. */
  async remove(id: string): Promise<void> {
    await apiClient.delete<null>(`${BASE}/${id}`);
  },
};
